import { PatchType } from '../../../types';
import {
  ExclusionOperationType,
  ExclusionTarget,
  EXCLUSION_OPERATION_TYPES,
} from '../../../services/exclusions/exclusions.types';
import { ExclusionTargetEntities } from '../hooks/useExclusionWorkflow';

/**
 * Turns the operation drawer's form state into an exclusion search target.
 *
 * The reconcile reuses `useOperationForm` and `OperationForm` for picking what to exclude rather than
 * growing a second set of product/variant/slot/modifier selectors. Those forms were built to produce a
 * patch operation, so this reads the entities back out of their state instead.
 *
 * Only the first selection is used for each entity. The drawer lets a user pick several variants or
 * modifiers at once because it stages one operation per selection; a search asks about exactly one target,
 * so a multi-select there is an ambiguity rather than a feature.
 */

/** The single-selection reader: these fields are arrays in form state even when one item is meant. */
const first = (value: any): any => (Array.isArray(value) ? value[0] : value) ?? null;

/** Which patch type a search for this operation type runs against. */
export const patchTypeForExclusionOperation = (operationType: string): PatchType | null => {
  if (operationType === 'RemoveBundleMenuPatchOperation') return PatchType.BUNDLE_EXCLUSION;
  return EXCLUSION_OPERATION_TYPES.includes(operationType as ExclusionOperationType)
    ? PatchType.PRODUCT_EXCLUSION
    : null;
};

/** The entity objects behind a target, so the review list can show names rather than UUIDs. */
export const entitiesFromFields = (fields: any): ExclusionTargetEntities => ({
  product: first(fields?.product) ?? undefined,
  variant: first(fields?.variants) ?? undefined,
  slot: fields?.slot ?? undefined,
  modifier: first(fields?.modifier) ?? undefined,
  weight: first(fields?.weights) ?? undefined,
  bundle: first(fields?.bundle) ?? undefined,
});

/**
 * Builds a target from form state, or null when the operation type is not a searchable exclusion.
 *
 * Ids may be missing - the form is filled in progressively - so the result is not necessarily complete.
 * Completeness is `isTargetComplete`'s job, and both the hook and the backend check it.
 */
export const targetFromFields = (
  operationType: string,
  fields: any
): ExclusionTarget | null => {
  if (!EXCLUSION_OPERATION_TYPES.includes(operationType as ExclusionOperationType)) return null;

  // `product` is the one field whose shape depends on the operation: OperationForm renders ProductSelect
  // with multiple={true} for RemoveProduct and multiple={false} for RemoveVariant, so it holds an array in
  // the first case and a bare object in the second. Reading .productId straight off it therefore worked for
  // every type except the one that actually needs it.
  const product = first(fields?.product);
  const variant = first(fields?.variants);
  const modifier = first(fields?.modifier);
  const weight = first(fields?.weights);
  const bundle = first(fields?.bundle);

  const target: ExclusionTarget = { operationType: operationType as ExclusionOperationType };

  switch (operationType) {
    case 'RemoveProductMenuPatchOperation':
      if (product?.productId) target.productId = product.productId;
      break;

    case 'RemoveVariantMenuPatchOperation':
      if (variant?.variantId) target.variantId = variant.variantId;
      break;

    case 'RemoveModifierMenuPatchOperation':
      if (modifier?.modifierId) target.modifierId = modifier.modifierId;
      break;

    case 'RemoveModifierFromSlotPatchOperation':
      if (fields?.slot?.slotId) target.slotId = fields.slot.slotId;
      if (modifier?.modifierId) target.modifierId = modifier.modifierId;
      break;

    case 'RemoveWeightFromSlotModifierMenuPatchOperation':
      if (fields?.slot?.slotId) target.slotId = fields.slot.slotId;
      if (modifier?.modifierId) target.modifierId = modifier.modifierId;
      // Both spellings occur: the response nests modifierWeightId, the form sometimes carries weightId.
      if (weight?.modifierWeightId || weight?.weightId) {
        target.weightId = weight.modifierWeightId ?? weight.weightId;
      }
      break;

    case 'SetModifierWeightIsExcludedFromVariantMenuPatchOperation':
      if (variant?.variantId) target.variantId = variant.variantId;
      if (fields?.slot?.slotId) target.slotId = fields.slot.slotId;
      if (modifier?.modifierId) target.modifierId = modifier.modifierId;
      if (weight?.modifierWeightId || weight?.weightId) {
        target.weightId = weight.modifierWeightId ?? weight.weightId;
      }
      break;

    case 'RemoveBundleMenuPatchOperation':
      if (bundle?.bundleId) target.bundleId = bundle.bundleId;
      break;

    default:
      return null;
  }

  return target;
};
